import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  Search,
  Tag,
  Clock,
  ArrowRight,
  Sparkles,
  MapPin,
  CheckCircle2,
  ChevronRight,
  ShieldCheck
} from 'lucide-react';
import { ExamCatalogEntry, examCatalog } from '../data/examCatalog';
import { UserProfile } from '../types';
import { getBlogPostsFromFirestore } from '../lib/firebase';

interface BlogPageProps {
  onSelectExam?: (entry: ExamCatalogEntry) => void;
  setActiveTab: (tab: string) => void;
  onOpenAuthModal: () => void;
  // The post slug the browser's address bar currently points to (from a direct
  // link, a bookmark, a shared URL, or back/forward navigation), or null when
  // showing the article list. Lets the real URL and the article view stay in sync.
  urlSlug?: string | null;
  // Called whenever the user opens a post or returns to the list from inside
  // this component, so the parent can push a real /blog or /blog/:slug address.
  onSlugChange?: (slug: string | null) => void;
}

interface BlogPost {
  id: string;
  examFamily: string;
  examCode: string;
  title: string;
  slug: string;
  summary: string;
  imageUrl?: string;
  contentMarkdown?: string;
  readTime: string;
  tags: string[];
  state: string;
  catalogEntryId: string;
  publishedDate: string;
}

// Minimal, dependency-free markdown renderer for blog article bodies.
// Supports: ## / ### headings, "- " and "1." lists, **bold**, and paragraphs.
function renderMarkdown(markdown: string): React.ReactNode[] {
  const renderInline = (text: string, keyPrefix: string): React.ReactNode => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) =>
      part.startsWith('**') && part.endsWith('**')
        ? <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>
        : <React.Fragment key={`${keyPrefix}-${i}`}>{part}</React.Fragment>
    );
  };

  const lines = (markdown || '').split('\n');
  const blocks: React.ReactNode[] = [];
  let listItems: string[] = [];
  let listType: 'ul' | 'ol' | null = null;

  const flushList = () => {
    if (listItems.length === 0) return;
    const key = `list-${blocks.length}`;
    if (listType === 'ol') {
      blocks.push(
        <ol key={key} className="list-decimal list-inside space-y-1.5 my-3 pl-1">
          {listItems.map((item, i) => <li key={i}>{renderInline(item, `${key}-${i}`)}</li>)}
        </ol>
      );
    } else {
      blocks.push(
        <ul key={key} className="list-disc list-inside space-y-1.5 my-3 pl-1">
          {listItems.map((item, i) => <li key={i}>{renderInline(item, `${key}-${i}`)}</li>)}
        </ul>
      );
    }
    listItems = [];
    listType = null;
  };

  lines.forEach((rawLine, idx) => {
    const line = rawLine.trim();
    const key = `b-${idx}`;

    if (line === '') { flushList(); return; }
    if (line.startsWith('### ')) { flushList(); blocks.push(<h3 key={key} className="text-base font-bold text-slate-900 mt-5 mb-2">{renderInline(line.slice(4), key)}</h3>); return; }
    if (line.startsWith('## ')) { flushList(); blocks.push(<h2 key={key} className="text-lg font-extrabold text-slate-900 mt-6 mb-2">{renderInline(line.slice(3), key)}</h2>); return; }

    const olMatch = line.match(/^\d+\.\s+(.*)/);
    if (olMatch) {
      if (listType !== 'ol') flushList();
      listType = 'ol';
      listItems.push(olMatch[1]);
      return;
    }
    if (line.startsWith('- ') || line.startsWith('* ')) {
      if (listType !== 'ul') flushList();
      listType = 'ul';
      listItems.push(line.slice(2));
      return;
    }

    flushList();
    blocks.push(<p key={key} className="text-sm text-slate-700 leading-relaxed my-2.5">{renderInline(line, key)}</p>);
  });
  flushList();
  return blocks;
}

// Seed articles shown until real posts are published from the admin dashboard.
// Once Firestore returns at least one published post, these are no longer used.
const FALLBACK_POSTS: BlogPost[] = [
  {
    id: 'cbest-math-strategy-2026',
    examFamily: 'CBEST',
    examCode: 'CBEST',
    title: 'CBEST Mathematics: 5 Crucial Strategy Rules for the 5-Choice Format',
    slug: 'cbest-mathematics-strategy-guide',
    summary: 'Unlike standard 4-option exams, CBEST Math utilizes 5 answer options. Master distractor elimination, estimation shortcuts, and word-problem translation.',
    readTime: '6 min read',
    tags: ['California', 'CBEST', 'Math Strategy', 'CTC Standards'],
    state: 'California',
    catalogEntryId: 'california',
    publishedDate: 'August 2026'
  },
  {
    id: 'cset-multiple-subjects-blueprint',
    examFamily: 'CSET Multiple Subjects',
    examCode: 'CSET',
    title: 'CSET Multiple Subjects: Subtest I, II, & III Domain Weightings Explained',
    slug: 'cset-multiple-subjects-blueprint-guide',
    summary: 'Complete breakdown of Reading, Language, History (Subtest I), Science & Math (Subtest II), and Physical Education, Human Development, & Arts (Subtest III).',
    readTime: '8 min read',
    tags: ['California', 'CSET', 'Constructed Response', 'Subtests'],
    state: 'California',
    catalogEntryId: 'california',
    publishedDate: 'August 2026'
  },
  {
    id: 'praxis-core-5713-5723-5733-guide',
    examFamily: 'Praxis Series',
    examCode: 'Praxis-Core',
    title: 'Praxis Core (5713, 5723, 5733): How to Clear Passing Thresholds Nationwide',
    slug: 'praxis-core-academic-skills-guide',
    summary: 'Everything you need to master Reading (5713), Writing argumentative essays (5723), and Mathematics (5733) across 40+ adopting state jurisdictions.',
    readTime: '7 min read',
    tags: ['ETS', 'Praxis Core', 'National', 'Essay Rubric'],
    state: 'Most Other States',
    catalogEntryId: 'most-other-states',
    publishedDate: 'August 2026'
  },
  {
    id: 'edtpa-elementary-commentary-blueprint',
    examFamily: 'edTPA',
    examCode: 'edTPA',
    title: 'edTPA Elementary: Crafting High-Scoring Planning and Assessment Commentaries',
    slug: 'edtpa-elementary-commentary-guide',
    summary: 'Step-by-step breakdown of Tasks 1, 2, 3, and Task 4 (Math Context), with rubric level 4 and 5 evidence expectations for prospective teachers.',
    readTime: '10 min read',
    tags: ['edTPA', 'Portfolio', 'Rubrics', 'California & National'],
    state: 'California',
    catalogEntryId: 'california',
    publishedDate: 'August 2026'
  },
  {
    id: 'nystce-eas-cst-strategy',
    examFamily: 'NYSTCE',
    examCode: 'NYSTCE',
    title: 'NYSTCE EAS & CST Multi-Subject: Essential Strategies for English Language Learners & SpEd',
    slug: 'nystce-eas-cst-strategy-guide',
    summary: 'How to approach Educating All Students (EAS) constructed response scenarios focusing on ELL accommodations and Students with Disabilities (SWD).',
    readTime: '7 min read',
    tags: ['New York', 'NYSTCE', 'EAS', 'CST'],
    state: 'New York',
    catalogEntryId: 'new-york',
    publishedDate: 'August 2026'
  },
  {
    id: 'texes-core-subjects-ec6-pacing',
    examFamily: 'TExES',
    examCode: 'TExES',
    title: 'TExES Core Subjects EC-6 (391): Pacing the 5 Subject-Area Subtests',
    slug: 'texes-core-subjects-ec6-pacing-guide',
    summary: 'Manage your 5 hours effectively across ELAR, Math, Social Studies, Science, and Fine Arts/Health/PE to secure your Texas standard certificate.',
    readTime: '6 min read',
    tags: ['Texas', 'TExES', 'TEA Standards', 'EC-6'],
    state: 'Texas',
    catalogEntryId: 'texas',
    publishedDate: 'August 2026'
  },
  {
    id: 'ftce-general-knowledge-breakdown',
    examFamily: 'FTCE',
    examCode: 'FTCE',
    title: 'FTCE General Knowledge: Essay Structure & Math Competency Standards',
    slug: 'ftce-general-knowledge-breakdown',
    summary: 'Key formulas and grammar conventions required to achieve passing scores across all four FTCE GK subtests in Florida.',
    readTime: '5 min read',
    tags: ['Florida', 'FTCE', 'General Knowledge', 'FLDOE'],
    state: 'Florida',
    catalogEntryId: 'florida',
    publishedDate: 'August 2026'
  },
  {
    id: 'ilts-gace-pect-comparison',
    examFamily: 'State Systems',
    examCode: 'State-Exams',
    title: 'State Certification Exam Matrix: Comparing ILTS, GACE, PECT, and Praxis Passing Benchmarks',
    slug: 'state-certification-exam-comparison-matrix',
    summary: 'A detailed jurisdictional crosswalk comparing test blueprints, score scaling ranges, and reciprocity rules across state departments of education.',
    readTime: '9 min read',
    tags: ['ILTS', 'GACE', 'PECT', 'Reciprocity'],
    state: 'National',
    catalogEntryId: 'most-other-states',
    publishedDate: 'August 2026'
  }
];

export const BlogPage: React.FC<BlogPageProps> = ({
  onSelectExam,
  setActiveTab,
  onOpenAuthModal,
  urlSlug = null,
  onSlugChange
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>(FALLBACK_POSTS);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const published = await getBlogPostsFromFirestore(false);
        if (isMounted && Array.isArray(published) && published.length > 0) {
          setBlogPosts(published.map(p => ({
            id: p.id,
            examFamily: p.examFamily,
            examCode: p.examCode || p.examFamily,
            title: p.title,
            slug: p.slug,
            summary: p.summary,
            imageUrl: p.imageUrl || '',
            contentMarkdown: p.contentMarkdown || '',
            readTime: p.readTime || '5 min read',
            tags: Array.isArray(p.tags) ? p.tags : [],
            state: p.state || 'National',
            catalogEntryId: p.catalogEntryId || '',
            publishedDate: p.publishedDate
          })));
        }
      } catch (err) {
        console.warn('BlogPage: falling back to seed articles, could not load Firestore posts:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, []);

  // Keep the open article in sync with the address bar: a direct link to
  // /blog/:slug, a bookmark, a shared URL, or the browser's back/forward
  // button all flow through the urlSlug prop rather than only local clicks.
  useEffect(() => {
    if (loading) return;
    if (urlSlug) {
      if (!selectedPost || selectedPost.slug !== urlSlug) {
        const match = blogPosts.find(p => p.slug === urlSlug);
        if (match) setSelectedPost(match);
      }
    } else if (selectedPost) {
      setSelectedPost(null);
    }
  }, [urlSlug, blogPosts, loading]);

  // Give each article its own real browser tab title (helps client-side
  // navigation and social shares match the per-post title the server also
  // sends on a fresh load of /blog/:slug).
  useEffect(() => {
    document.title = selectedPost
      ? `${selectedPost.title} | Pre-Praxis Prep`
      : 'Pre-Praxis Prep — State Certification Practice & AI Tutoring';
  }, [selectedPost]);

  const categories = React.useMemo(() => {
    const unique = Array.from(new Set(blogPosts.map(p => p.examFamily))).filter(Boolean);
    return ['all', ...unique];
  }, [blogPosts]);

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'all' || post.examFamily === selectedCategory;
    const matchesSearch = searchQuery.trim() === '' ||
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.tags.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const handleStartExamFromBlog = (catalogId: string) => {
    const matched = examCatalog.find(e => e.id === catalogId);
    if (matched && onSelectExam) {
      onSelectExam(matched);
    } else {
      setActiveTab('papers');
    }
  };

  // Opening/closing an article both updates the visible view and tells the
  // parent to push the matching real URL (/blog/:slug or back to /blog).
  const openPost = (post: BlogPost) => {
    setSelectedPost(post);
    onSlugChange?.(post.slug);
  };

  const closePost = () => {
    setSelectedPost(null);
    onSlugChange?.(null);
  };

  if (selectedPost) {
    return (
      <div className="max-w-3xl mx-auto py-6 sm:py-10 space-y-6">
        <button
          onClick={closePost}
          className="text-xs font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 cursor-pointer"
        >
          <ArrowRight className="w-3.5 h-3.5 rotate-180" />
          <span>Back to Articles</span>
        </button>

        {selectedPost.imageUrl && (
          <img
            src={selectedPost.imageUrl}
            alt={selectedPost.title}
            className="w-full h-56 sm:h-72 object-cover rounded-2xl border border-slate-200"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        )}

        <div className="flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-indigo-50 border border-indigo-100 text-indigo-700 font-bold text-[11px]">
            <MapPin className="w-3 h-3 text-indigo-600" />
            {selectedPost.state}
          </span>
          <span className="text-[11px] text-slate-400 flex items-center gap-1 font-medium">
            <Clock className="w-3.5 h-3.5 text-slate-400" />
            {selectedPost.readTime}
          </span>
          <span className="text-[11px] text-slate-400 font-semibold">{selectedPost.publishedDate}</span>
        </div>

        <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight leading-snug">
          {selectedPost.title}
        </h1>

        <p className="text-sm text-slate-600 leading-relaxed font-medium">{selectedPost.summary}</p>

        <div className="flex flex-wrap gap-1.5">
          {selectedPost.tags.map((tag, i) => (
            <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-medium">#{tag}</span>
          ))}
        </div>

        <div className="border-t border-slate-100 pt-4">
          {selectedPost.contentMarkdown
            ? renderMarkdown(selectedPost.contentMarkdown)
            : <p className="text-sm text-slate-400 italic">Full article coming soon.</p>}
        </div>

        <div className="pt-4 border-t border-slate-100">
          <button
            onClick={() => handleStartExamFromBlog(selectedPost.catalogEntryId)}
            className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm px-5 py-2.5 rounded-xl transition cursor-pointer"
          >
            <span>Practice {selectedPost.examFamily}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-10 py-6 sm:py-10">
      {/* Header */}
      <div className="space-y-4 text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-bold">
          <BookOpen className="w-3.5 h-3.5 text-indigo-600" />
          <span>Educator Preparation Resources & Guides</span>
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 tracking-tight">
          Exam Blueprints, Domain Strategies & Pass Guides
        </h1>
        <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
          In-depth articles curated by certified educators and test design specialists covering CBEST, CSET, NYSTCE, TExES, FTCE, and Praxis Series exams.
        </p>

        {/* Search Input */}
        <div className="relative max-w-md mx-auto pt-2">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search articles by exam, subject, or domain..."
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 shadow-2xs transition"
          />
        </div>
      </div>

      {/* Category Filter Pills */}
      <div className="flex items-center justify-start sm:justify-center gap-2 overflow-x-auto pb-2 scrollbar-thin">
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap cursor-pointer ${
              selectedCategory === cat
                ? 'bg-indigo-600 text-white shadow-xs'
                : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            {cat === 'all' ? 'All Exam Families' : cat}
          </button>
        ))}
      </div>

      {/* Blog Cards Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-slate-100 h-56 rounded-2xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPosts.map((post) => (
            <article
              key={post.id}
              className="bg-white rounded-2xl border border-slate-200 shadow-xs hover:shadow-md hover:border-indigo-300 transition flex flex-col justify-between overflow-hidden group"
            >
              {post.imageUrl && (
                <button
                  onClick={() => openPost(post)}
                  className="block w-full h-40 overflow-hidden bg-slate-100 cursor-pointer"
                >
                  <img
                    src={post.imageUrl}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                    onError={(e) => { (e.currentTarget.closest('button') as HTMLElement)?.style.setProperty('display', 'none'); }}
                  />
                </button>
              )}
              <div className="p-6 space-y-3.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-indigo-50 border border-indigo-100 text-indigo-700 font-bold text-[11px]">
                    <MapPin className="w-3 h-3 text-indigo-600" />
                    {post.state}
                  </span>
                  <span className="text-[11px] text-slate-400 flex items-center gap-1 font-medium">
                    <Clock className="w-3.5 h-3.5 text-slate-400" />
                    {post.readTime}
                  </span>
                </div>

                <h2
                  onClick={() => openPost(post)}
                  className="text-base sm:text-lg font-bold text-slate-900 group-hover:text-indigo-600 transition leading-snug cursor-pointer"
                >
                  {post.title}
                </h2>

                <p className="text-xs text-slate-600 leading-relaxed line-clamp-3">
                  {post.summary}
                </p>

                <button
                  onClick={() => openPost(post)}
                  className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 cursor-pointer"
                >
                  <span>Read Full Article</span>
                  <ArrowRight className="w-3 h-3" />
                </button>

                {/* Tags */}
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {post.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-medium"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Card Footer CTA */}
              <div className="p-4 sm:px-6 bg-slate-50/60 border-t border-slate-100 flex items-center justify-between">
                <span className="text-[11px] text-slate-400 font-semibold">
                  {post.publishedDate}
                </span>
                <button
                  onClick={() => handleStartExamFromBlog(post.catalogEntryId)}
                  className="text-xs font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 group-hover:translate-x-0.5 transition cursor-pointer"
                >
                  <span>Practice {post.examFamily}</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {!loading && filteredPosts.length === 0 && (
        <div className="text-center py-12 bg-white rounded-2xl border border-slate-200 p-8">
          <p className="text-sm font-semibold text-slate-600">No articles matched your filter.</p>
          <button
            onClick={() => { setSelectedCategory('all'); setSearchQuery(''); }}
            className="mt-3 text-xs font-bold text-indigo-600 hover:underline cursor-pointer"
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
};
